import { formatEnum } from '@/lib/format';

export type CarePlanServiceDeliveryModel = 'OWN_WORKFORCE' | 'THIRD_PARTY' | 'SELF_MANAGED';

export type CarePlanWorkerClassification =
  | 'CARE_WORKER'
  | 'RN'
  | 'EN'
  | 'NP'
  | 'HCA1'
  | 'HCA2'
  | 'HCA3'
  | 'HCA4'
  | 'HCA5'
  | 'HCA6'
  | 'L1'
  | 'L2'
  | 'L3'
  | 'L4'
  | 'L5'
  | 'L6'
  | 'L7'
  | 'L8';

export type CarePlanServiceEvidenceProfile =
  | 'STANDARD_SERVICE_NOTE'
  | 'CLINICAL_SERVICE_NOTE'
  | 'ITEM_OR_MILESTONE_EVIDENCE'
  | 'THIRD_PARTY_DELIVERY_EVIDENCE'
  | 'SELF_MANAGED_DELIVERY_EVIDENCE';

export type CarePlanServiceExecutionBlocker = {
  inclusionId?: string | null;
  serviceCode?: string | null;
  serviceLabel: string;
  code: string;
};

export type CarePlanServiceExecutionGoalLink = {
  id: string;
  title: string;
  category?: string | null;
  status?: string | null;
};

export type CarePlanServiceExecutionRow = {
  id: string;
  serviceCode: string;
  serviceCategory: string;
  serviceLabel: string;
  sahServiceCatalogueCode?: string | null;
  sahServiceType?: string | null;
  unitType: string;
  unitCost: number;
  frequencyPerPeriod: number;
  contributionPercentage?: number | null;
  startDate: string;
  endDate?: string | null;
  fundingSourceCode?: string | null;
  fundingProgramAllocation?: Record<string, unknown> | null;
  pricingEvidenceMode?: string | null;
  scheduleIntent?: Record<string, unknown> | null;
  deliveryModel?: CarePlanServiceDeliveryModel | null;
  deliveryProviderId?: string | null;
  deliveryProviderName?: string | null;
  evidenceProfileCode?: CarePlanServiceEvidenceProfile | null;
  responsibilityNotes?: string | null;
  contingencyNotes?: string | null;
  workerClassificationRequired?: string | null;
  goalLinks: CarePlanServiceExecutionGoalLink[];
  goalLinkCount: number;
  openDeliveryCount: number;
  futureShiftCount: number;
  operationalChangeGuarded: boolean;
  contractVersion?: string | null;
  confirmedAt?: string | null;
  complete: boolean;
  blockers: string[];
  warnings: string[];
};

export type CarePlanServiceExecutionProjection = {
  schemaVersion: 1;
  contractVersion: 'care-plan-service-execution/phase3.v1';
  carePlanId: string;
  carePlanVersion: number;
  clientId: string;
  asOf: string;
  applicable: boolean;
  authority: 'care_plan_service_execution_lifecycle_guard';
  ready: boolean;
  inclusionCount: number;
  completeCount: number;
  blockerCount: number;
  blockers: CarePlanServiceExecutionBlocker[];
  rows: CarePlanServiceExecutionRow[];
};

export const CARE_PLAN_SERVICE_DELIVERY_MODEL_OPTIONS: Array<{
  value: CarePlanServiceDeliveryModel;
  label: string;
  description: string;
}> = [
  {
    value: 'OWN_WORKFORCE',
    label: 'Provider workforce',
    description: 'Delivered by the provider’s own rostered workforce.',
  },
  {
    value: 'THIRD_PARTY',
    label: 'Engaged third party',
    description: 'Delivered by an active provider-managed delivery partner.',
  },
  {
    value: 'SELF_MANAGED',
    label: 'Self-managed',
    description: 'Arranged by the participant within the provider’s accountable plan.',
  },
];

/**
 * Canonical vocabulary accepted by sah_normalize_worker_classification_requirement.
 * Keep service preferences (for example language, gender or falls awareness) in
 * worker alerts/preferences rather than combining them with this roster gate.
 */
export const CARE_PLAN_WORKER_CLASSIFICATION_OPTIONS: Array<{
  value: CarePlanWorkerClassification;
  label: string;
}> = [
  { value: 'CARE_WORKER', label: 'Care worker' },
  { value: 'RN', label: 'Registered nurse (RN)' },
  { value: 'EN', label: 'Enrolled nurse (EN)' },
  { value: 'NP', label: 'Nurse practitioner (NP)' },
  { value: 'HCA1', label: 'Home-care aged classification 1 (HCA1)' },
  { value: 'HCA2', label: 'Home-care aged classification 2 (HCA2)' },
  { value: 'HCA3', label: 'Home-care aged classification 3 (HCA3)' },
  { value: 'HCA4', label: 'Home-care aged classification 4 (HCA4)' },
  { value: 'HCA5', label: 'Home-care aged classification 5 (HCA5)' },
  { value: 'HCA6', label: 'Home-care aged classification 6 (HCA6)' },
  { value: 'L1', label: 'Roster capability level 1 (L1)' },
  { value: 'L2', label: 'Roster capability level 2 (L2)' },
  { value: 'L3', label: 'Roster capability level 3 (L3)' },
  { value: 'L4', label: 'Roster capability level 4 (L4)' },
  { value: 'L5', label: 'Roster capability level 5 (L5)' },
  { value: 'L6', label: 'Roster capability level 6 (L6)' },
  { value: 'L7', label: 'Roster capability level 7 (L7)' },
  { value: 'L8', label: 'Roster capability level 8 (L8)' },
];

/**
 * New authoring picks for care-plan gate columns (demand model A).
 * Industrial HCA/L floors remain recognised for legacy display/normalise only.
 * Free-prose skill notes belong in preferences/notes — never this column.
 */
export const CARE_PLAN_WORKER_CLASSIFICATION_SELECTABLE: Array<{
  value: CarePlanWorkerClassification;
  label: string;
}> = [
  { value: 'CARE_WORKER', label: 'Care worker' },
  { value: 'RN', label: 'Registered nurse (RN)' },
  { value: 'EN', label: 'Enrolled nurse (EN)' },
  { value: 'NP', label: 'Nurse practitioner (NP)' },
];

/**
 * Display label for a stored worker-classification requirement.
 *
 * The column holds canonical tokens (CARE_WORKER, RN, EN, NP, HCA1-HCA6, L1-L8) and
 * historically also stored aliases such as PERSONAL_CARE_WORKER. Rendering the raw
 * token breaks the rule against showing enum tokens in user-facing UI — the
 * care-plan service audit table was displaying "CARE_WORKER" in the worker column
 * directly beside a row reading "Any qualified worker".
 *
 * Normalises first so aliases resolve to the same label as the canonical token,
 * and falls back to the general enum formatter for anything unrecognised rather
 * than showing nothing.
 */
export function carePlanWorkerClassificationLabel(
  value: string | null | undefined,
  fallback = 'Any qualified worker',
): string {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const normalised = normaliseCarePlanWorkerClassification(raw);
  const option = CARE_PLAN_WORKER_CLASSIFICATION_OPTIONS.find(
    (candidate) => candidate.value === normalised,
  );
  if (option) return option.label;
  // Unrecognised: humanise rather than leak the token (e.g. legacy free text).
  return raw
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

const CARE_WORKER_ALIASES = new Set([
  'HOME_CARE',
  'HOME_CARE_WORKER',
  'CARE_WORKER',
  'CARE_WORKER_LEVEL_1',
  'DIRECT_CARE_WORKER',
  'SUPPORT_WORKER',
  'PERSONAL_CARE',
  'PERSONAL_CARE_WORKER',
  'PCW',
  'AIN',
  'ASSISTANT_IN_NURSING',
]);

/** Mirrors the database normalizer so existing recognized aliases remain editable. */
export function normaliseCarePlanWorkerClassification(
  value: string | null | undefined,
): CarePlanWorkerClassification | '' {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s.-]+/g, '_');
  if (!normalized) return '';
  if (CARE_WORKER_ALIASES.has(normalized)) return 'CARE_WORKER';
  if (normalized === 'RN' || normalized === 'REGISTERED_NURSE') return 'RN';
  if (normalized === 'EN' || normalized === 'ENROLLED_NURSE') return 'EN';
  if (normalized === 'NP' || normalized === 'NURSE_PRACTITIONER') return 'NP';
  if (/^L[1-8]$/.test(normalized)) return normalized as CarePlanWorkerClassification;
  if (/^LEVEL_[1-8]$/.test(normalized)) {
    return `L${normalized.at(-1)}` as CarePlanWorkerClassification;
  }
  if (/^(SCHADS|SAH)_(LEVEL_)?[1-8]$/.test(normalized)) {
    return `L${normalized.at(-1)}` as CarePlanWorkerClassification;
  }
  if (/^HCA[1-6]$/.test(normalized)) return normalized as CarePlanWorkerClassification;
  // Legacy mis-map of HCA7/HCA8 (not SCHADS home-care levels) → L-series capability.
  if (/^HCA[7-8]$/.test(normalized)) {
    return `L${normalized.at(-1)}` as CarePlanWorkerClassification;
  }
  return '';
}

/**
 * Fail-closed authoring for inclusion WCR: blank is allowed (no requirement);
 * non-blank must normalise to a canonical token. Free prose is rejected.
 */
export function resolveCarePlanWorkerClassificationForAuthoring(
  value: string | null | undefined,
): { ok: true; value: CarePlanWorkerClassification | null } | { ok: false; error: string } {
  const raw = String(value ?? '').trim();
  if (!raw) return { ok: true, value: null };
  const normalised = normaliseCarePlanWorkerClassification(raw);
  if (!normalised) {
    return {
      ok: false,
      error:
        'Worker skill requirement must be a short roster token (Care worker, RN, EN or NP). Put language, gender or other preferences in notes — not this gate field.',
    };
  }
  return { ok: true, value: normalised };
}

/**
 * Starting text for the shared-responsibility fields on a brokered or
 * self-managed service.
 *
 * These two fields are required for THIRD_PARTY and SELF_MANAGED delivery, and
 * composing them from scratch is what stalled a real care plan: for a brokered
 * podiatry service the honest contingency is "book a different podiatrist", and
 * a coordinator asked to write that as prose stopped and left the plan
 * unfinished. The requirement is sound — for care someone else delivers, this
 * text is the record that the provider stays accountable — so the fix is to make
 * it a confirmation rather than an essay.
 *
 * Returns null for OWN_WORKFORCE, where neither field is required.
 *
 * Deliberately avoids naming the provider organisation. The component has the
 * delivery provider's name but not the tenant's own, and threading it through
 * would mean another query for wording that reads correctly without it.
 */
export function suggestedSharedResponsibilityNotes(input: {
  deliveryModel: CarePlanServiceDeliveryModel;
  deliveryProviderName?: string | null;
}): { responsibilityNotes: string; contingencyNotes: string } | null {
  const provider = String(input.deliveryProviderName ?? '').trim();

  if (input.deliveryModel === 'THIRD_PARTY') {
    const deliverer = provider || 'the engaged third-party provider';
    return {
      responsibilityNotes:
        `Arranged, supervised and recorded by the care provider. Delivered by ${deliverer}. ` +
        'The care provider escalates any concern and remains accountable for this service.',
      contingencyNotes:
        `If ${deliverer} cannot deliver as scheduled, the care provider arranges an ` +
        'alternative provider and notifies the participant, rather than the service simply ' +
        'not happening.',
    };
  }

  if (input.deliveryModel === 'SELF_MANAGED') {
    return {
      responsibilityNotes:
        'Arranged directly by the participant within the care provider’s accountable plan. ' +
        'The care provider records delivery, monitors outcomes and escalates any concern.',
      contingencyNotes:
        'If the arranged service cannot proceed, the participant notifies the care provider, ' +
        'which reviews the arrangement and offers an alternative.',
    };
  }

  return null;
}

const EVIDENCE_PROFILE_LABELS: Record<CarePlanServiceEvidenceProfile, string> = {
  STANDARD_SERVICE_NOTE: 'Service summary or progress note',
  CLINICAL_SERVICE_NOTE: 'Clinical service note',
  ITEM_OR_MILESTONE_EVIDENCE: 'Item or milestone completion evidence',
  THIRD_PARTY_DELIVERY_EVIDENCE: 'Third-party delivery evidence',
  SELF_MANAGED_DELIVERY_EVIDENCE: 'Self-managed delivery evidence',
};

const BLOCKER_LABELS: Record<string, string> = {
  SERVICE_INCLUSION_REQUIRED: 'Add at least one agreed service',
  EXECUTION_DETAIL_NOT_CONFIRMED: 'Confirm delivery details',
  GOAL_LINK_REQUIRED: 'Link the need or goal this service addresses',
  SCHEDULE_INTENT_REQUIRED: 'Set a valid schedule cadence and pattern',
  DELIVERY_MODEL_REQUIRED: 'Choose who will deliver the service',
  EVIDENCE_PROFILE_REQUIRED: 'Confirm the delivery evidence path',
  WORKER_REQUIREMENT_REQUIRED: 'Record the required worker classification or capability',
  PRICE_EVIDENCE_REQUIRED: 'Complete provider price evidence',
  DELIVERY_PROVIDER_REQUIRED: 'Choose an active delivery provider',
  RESPONSIBILITY_REQUIRED: 'Clarify provider and participant responsibility',
  CONTINGENCY_REQUIRED: 'Record the contingency if delivery cannot proceed',
  THIRD_PARTY_DATA_SHARING_CONSENT_REQUIRED: 'Record third-party data-sharing consent',
  SELF_MANAGEMENT_EXPLANATION_REQUIRED: 'Record that self-management was explained',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isGoalLink(value: unknown): value is CarePlanServiceExecutionGoalLink {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    (value.category == null || typeof value.category === 'string') &&
    (value.status == null || typeof value.status === 'string')
  );
}

function isExecutionRow(value: unknown): value is CarePlanServiceExecutionRow {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.serviceCode === 'string' &&
    typeof value.serviceCategory === 'string' &&
    typeof value.serviceLabel === 'string' &&
    typeof value.unitType === 'string' &&
    typeof value.unitCost === 'number' &&
    typeof value.frequencyPerPeriod === 'number' &&
    typeof value.startDate === 'string' &&
    Array.isArray(value.goalLinks) &&
    value.goalLinks.every(isGoalLink) &&
    typeof value.goalLinkCount === 'number' &&
    typeof value.openDeliveryCount === 'number' &&
    typeof value.futureShiftCount === 'number' &&
    typeof value.operationalChangeGuarded === 'boolean' &&
    typeof value.complete === 'boolean' &&
    isStringArray(value.blockers) &&
    isStringArray(value.warnings)
  );
}

function isProjectionBlocker(value: unknown): value is CarePlanServiceExecutionBlocker {
  return (
    isRecord(value) &&
    typeof value.serviceLabel === 'string' &&
    typeof value.code === 'string' &&
    (value.inclusionId == null || typeof value.inclusionId === 'string') &&
    (value.serviceCode == null || typeof value.serviceCode === 'string')
  );
}

export function readCarePlanServiceExecutionProjection(
  value: unknown,
): CarePlanServiceExecutionProjection | null {
  if (!isRecord(value)) return null;
  if (
    value.schemaVersion !== 1 ||
    value.contractVersion !== 'care-plan-service-execution/phase3.v1' ||
    typeof value.carePlanId !== 'string' ||
    typeof value.carePlanVersion !== 'number' ||
    typeof value.clientId !== 'string' ||
    typeof value.asOf !== 'string' ||
    typeof value.applicable !== 'boolean' ||
    value.authority !== 'care_plan_service_execution_lifecycle_guard' ||
    typeof value.ready !== 'boolean' ||
    typeof value.inclusionCount !== 'number' ||
    typeof value.completeCount !== 'number' ||
    typeof value.blockerCount !== 'number' ||
    !Array.isArray(value.rows) ||
    !value.rows.every(isExecutionRow) ||
    !Array.isArray(value.blockers) ||
    !value.blockers.every(isProjectionBlocker)
  ) {
    return null;
  }
  return value as unknown as CarePlanServiceExecutionProjection;
}

export function carePlanServiceExecutionBlockerLabel(code: string): string {
  return BLOCKER_LABELS[code] ?? code.toLowerCase().replaceAll('_', ' ');
}

const BLOCKER_LABEL_VALUES = new Set(Object.values(BLOCKER_LABELS));

/**
 * True when a readiness-blocker string was emitted by
 * carePlanServiceExecutionReadinessLabels — either a bare requirement label or
 * a "<service label>: <requirement>" per-inclusion variant. Used to route these
 * blockers to the Services lane instead of the terminal activate anchor.
 */
export function isCarePlanServiceExecutionBlockerLabel(blocker: string): boolean {
  const raw = blocker.trim();
  if (BLOCKER_LABEL_VALUES.has(raw)) return true;
  // Requirement labels never contain ': ', so the last separator always splits
  // "<service label>: <requirement>" correctly even for exotic service labels.
  const separatorIndex = raw.lastIndexOf(': ');
  if (separatorIndex === -1) return false;
  return BLOCKER_LABEL_VALUES.has(raw.slice(separatorIndex + 2));
}

/**
 * A blocker's serviceLabel is whatever the projection carried. The readiness
 * SQL builds it as
 * `coalesce(sah_service_display_name, catalogue_display_name, service_code)`,
 * so it is a readable name ("Assistance with self-care and activities of daily
 * living") whenever either display name exists, and otherwise the bare service
 * code — which used to surface to operators as "PERSONAL_CARE: choose who will
 * deliver the service".
 *
 * Only that last case may be rewritten. Treating every screaming-snake string
 * as an enum token would send a provider's own stored display name through a
 * lossy title-caser: a legitimate `CARE_TEAM_PLUS` display name would print as
 * "Care Team Plus". The display-name columns are unrestricted text, so the test
 * is identity against the service code, not shape.
 */
function carePlanServiceExecutionBlockerServiceLabel(
  blocker: CarePlanServiceExecutionBlocker,
): string {
  const label = (blocker.serviceLabel ?? '').trim();
  const code = (blocker.serviceCode ?? '').trim();
  if (!label) return formatEnum(code || 'service');
  const isTheServiceCode = code !== '' && label.toUpperCase() === code.toUpperCase();
  return isTheServiceCode ? formatEnum(label) : label;
}

export function carePlanServiceExecutionReadinessLabels(
  projection: CarePlanServiceExecutionProjection | null,
): string[] {
  if (!projection?.applicable) return [];
  const labels = projection.blockers.map((blocker) => {
    const requirement = carePlanServiceExecutionBlockerLabel(blocker.code);
    return blocker.inclusionId
      ? `${carePlanServiceExecutionBlockerServiceLabel(blocker)}: ${requirement}`
      : requirement;
  });
  return Array.from(new Set(labels));
}

export function carePlanServiceEvidenceProfileLabel(
  code: CarePlanServiceEvidenceProfile | string | null | undefined,
): string {
  if (!code) return 'Not confirmed';
  return EVIDENCE_PROFILE_LABELS[code as CarePlanServiceEvidenceProfile] ?? code;
}

export function carePlanServiceDeliveryModelLabel(
  model: CarePlanServiceDeliveryModel | string | null | undefined,
): string {
  return (
    CARE_PLAN_SERVICE_DELIVERY_MODEL_OPTIONS.find((option) => option.value === model)?.label ??
    'Delivery not confirmed'
  );
}

export function expectedCarePlanServiceEvidenceProfile(input: {
  serviceCode?: string | null;
  serviceCategory?: string | null;
  deliveryModel: CarePlanServiceDeliveryModel;
}): CarePlanServiceEvidenceProfile {
  if (input.deliveryModel === 'THIRD_PARTY') return 'THIRD_PARTY_DELIVERY_EVIDENCE';
  if (input.deliveryModel === 'SELF_MANAGED') return 'SELF_MANAGED_DELIVERY_EVIDENCE';
  const serviceCode = String(input.serviceCode ?? '').toUpperCase();
  if (['ASSISTIVE_TECHNOLOGY', 'HOME_MODIFICATION'].includes(serviceCode)) {
    return 'ITEM_OR_MILESTONE_EVIDENCE';
  }
  if (
    String(input.serviceCategory ?? '').toUpperCase() === 'CLINICAL_CARE' ||
    ['NURSING', 'ALLIED_HEALTH'].includes(serviceCode)
  ) {
    return 'CLINICAL_SERVICE_NOTE';
  }
  return 'STANDARD_SERVICE_NOTE';
}

export function carePlanServiceRequiresWorker(serviceCode: string | null | undefined): boolean {
  return !['ASSISTIVE_TECHNOLOGY', 'HOME_MODIFICATION'].includes(
    String(serviceCode ?? '').toUpperCase(),
  );
}
